/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React from 'react';
import { StyleSheet, View } from 'react-native';
import { breakpoints, useDeviceInfo } from '@airasia-rn/ui';
import { parseDensitySrcSet } from '../../utils/responsiveImage';

export type HeroBannerBackgroundImageSet = { src?: string; srcSet?: string };

const getViewportImageSrc = ({
  fallbackSrc,
  srcSet,
  width,
}: {
  fallbackSrc?: string;
  srcSet?: string;
  width: number;
}) => {
  const images = parseDensitySrcSet(srcSet);

  if (width < breakpoints.md) {
    return images.mobile ?? fallbackSrc;
  }

  if (width < breakpoints.xl) {
    return images.tablet ?? images.mobile ?? fallbackSrc;
  }

  return images.desktop ?? images.tablet ?? images.mobile ?? fallbackSrc;
};

const getWebViewportWidth = (fallbackWidth: number) => {
  if (typeof window === 'undefined') {
    return fallbackWidth;
  }

  return window.innerWidth || fallbackWidth;
};

const HeroBannerBackground = ({
  backgroundImage,
  containerWidth,
  imageStyle,
  children,
}: {
  backgroundImage?: HeroBannerBackgroundImageSet;
  containerWidth: number;
  imageStyle?: React.CSSProperties;
  children: React.ReactNode;
}) => {
  const [hasMounted, setHasMounted] = React.useState(false);
  const { isMobileWidth } = useDeviceInfo();

  React.useEffect(() => {
    setHasMounted(true);
  }, []);

  const src = backgroundImage?.src;
  const srcSet = backgroundImage?.srcSet;
  // Pre-measure the carousel reports width 0, which used to select the
  // mobile asset during SSR on desktop and then swap it for the desktop URL
  // after hydration (a fresh fetch = visible hero flash). The device hint
  // makes isMobileWidth SSR-stable, so fall back to a width on the correct
  // side of the breakpoints and the SSR-selected asset is already final.
  const hintWidth = isMobileWidth ? breakpoints.md - 1 : breakpoints.xl;
  const preMeasureWidth = containerWidth || hintWidth;
  const webViewportWidth = hasMounted
    ? getWebViewportWidth(preMeasureWidth)
    : preMeasureWidth;
  const selectedWebSrc = getViewportImageSrc({
    fallbackSrc: src,
    srcSet,
    width: webViewportWidth,
  });
  const webImageStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    pointerEvents: 'none',
    ...imageStyle,
  };

  return (
    <View style={StyleSheet.absoluteFill}>
      {selectedWebSrc ? (
        <img
          src={selectedWebSrc}
          alt=""
          style={webImageStyle}
          fetchPriority="high"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      ) : null}
      {children}
    </View>
  );
};

export default HeroBannerBackground;
